import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FolderOpenIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  PrinterIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { BASE_URL } from "../../config";
import { openPhysicianPrintPreview } from "./physicianPrintTemplate";

const TAB_LABELS = {
  information: "Information",
  contact: "Contact",
  bank: "Bank",
  address: "Address",
  license: "License",
  maintenance: "Maintenance",
  provider_affiliates: "Provider Affiliates",
  physician_trns: "Physician TRNs",
};

const CIVIL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Divorced", label: "Divorced" },
  { value: "Separated", label: "Separated" },
  { value: "Widowed", label: "Widowed" },
  { value: "Living common law", label: "Living common law" },
  { value: "Married", label: "Married" },
];

const GENDER_OPTIONS = [
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
];

const VATABLE_OPTIONS = [
  { value: "0", label: "False" },
  { value: "1", label: "True" },
];

const MAINTENANCE_UPDATE_OPTIONS = [
  { value: "suspension", label: "Suspension" },
  { value: "reactivation", label: "Reactivation" },
  { value: "termination", label: "Termination" },
  { value: "extension", label: "Extension" },
];

const controlClass =
  "mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 text-gray-900 text-xs cursor-not-allowed disabled:opacity-100";

const tableControlClass =
  "block w-full min-w-[140px] border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 text-gray-900 text-xs cursor-not-allowed disabled:opacity-100";

const readOnlySelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    borderColor: "#e5e7eb",
    backgroundColor: "#f9fafb",
    boxShadow: "none",
    cursor: "not-allowed",
    fontSize: "0.75rem",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "30px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#6b7280",
  }),
};

const isBlank = (value) => value === null || value === undefined || value === "";

const toText = (value) => (isBlank(value) ? "" : String(value));

const normalizeDateValue = (value) => {
  const text = toText(value);
  const match = text.match(/^\d{4}-\d{2}-\d{2}/);
  if (!match || match[0] === "0000-00-00") return "";
  return match[0];
};

const getInputValue = (value, type) => {
  if (isBlank(value)) return "";
  if (type === "date") return normalizeDateValue(value);
  return String(value);
};

const normalizeGenderValue = (value) => {
  const text = toText(value).toLowerCase();
  if (text === "male") return "m";
  if (text === "female") return "f";
  return text;
};

const isTruthy = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  toText(value).toLowerCase() === "true" ||
  toText(value).toLowerCase() === "yes";

const normalizeVatableValue = (value) => (isTruthy(value) ? "1" : "0");

const isFileUrlExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

const LEGACY_UPLOAD_BASE_URL = "https://allaccess.net.ph/upload_doc";
const LEGACY_UPLOAD_PATH_REGEX = /\/upload_doc\//i;

const isLegacyUploadUrl = (value) =>
  LEGACY_UPLOAD_PATH_REGEX.test(String(value || ""));

const looksLikeLegacyFileName = (value) =>
  /\.[a-z0-9]{2,8}(\?|#|$)/i.test(String(value || "").trim());

const buildLegacyUploadUrl = (folder, rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return null;

  if (/^https?:\/\//i.test(value)) {
    return isLegacyUploadUrl(value) ? value : null;
  }

  if (!looksLikeLegacyFileName(value)) return null;

  const normalizedFolder = String(folder || "")
    .trim()
    .replace(/^\/+|\/+$/g, "");
  if (!normalizedFolder) return null;

  let normalizedFileName = value;
  try {
    normalizedFileName = decodeURIComponent(value);
  } catch (error) {
    normalizedFileName = value;
  }

  return `${LEGACY_UPLOAD_BASE_URL}/${encodeURIComponent(normalizedFolder)}/${encodeURIComponent(normalizedFileName)}`;
};

const resolveLegacyUploadUrl = ({ folder, fallbackUrl = "", candidates = [] }) => {
  const fallback = String(fallbackUrl || "").trim();
  if (isLegacyUploadUrl(fallback)) {
    return fallback;
  }

  for (const candidate of candidates) {
    const legacyUrl = buildLegacyUploadUrl(folder, candidate);
    if (legacyUrl) return legacyUrl;
  }

  return null;
};

const formatProviderOptions = (providerList = []) =>
  providerList
    .filter((provider) => provider?.provider_code)
    .map((provider) => ({
      value: provider.provider_code,
      label: `${provider.provider_name || provider.provider_code} (${provider.provider_code})`,
      name: provider.provider_name || "",
    }));

const findSelectedOption = (options = [], value, fallbackLabel) => {
  if (isBlank(value)) return null;

  const textValue = String(value);
  const selected = options.find(
    (option) => String(option.value) === textValue || String(option.label) === textValue
  );

  return selected || {
    value: textValue,
    label: fallbackLabel || textValue,
  };
};

const withSelectedOption = (options = [], selected) => {
  if (!selected) return options;

  const hasSelected = options.some((option) => String(option.value) === String(selected.value));
  return hasSelected ? options : [selected, ...options];
};

const FieldLabel = ({ label }) => (
  <label className="block text-xs font-medium text-gray-700">{label}</label>
);

const ReadOnlyInput = ({ label, value, type = "text", className = "" }) => (
  <div className={className}>
    <FieldLabel label={label} />
    <input
      type={type}
      value={getInputValue(value, type)}
      readOnly
      disabled
      placeholder="N/A"
      className={controlClass}
    />
  </div>
);

const ReadOnlyTextarea = ({ label, value, className = "", rows = 3 }) => (
  <div className={className}>
    <FieldLabel label={label} />
    <textarea
      value={toText(value)}
      readOnly
      disabled
      placeholder="N/A"
      rows={rows}
      className={`${controlClass} resize-none`}
    />
  </div>
);

const ReadOnlyCheckbox = ({ label, checked }) => (
  <div>
    <FieldLabel label={label} />
    <div className="mt-1 flex items-center gap-2">
      <input
        type="checkbox"
        checked={isTruthy(checked)}
        readOnly
        disabled
        className="h-5 w-5 rounded border-gray-300 text-blue-600 cursor-not-allowed disabled:opacity-100"
      />
      <span className="text-xs text-gray-700">{isTruthy(checked) ? "Yes" : "No"}</span>
    </div>
  </div>
);

const ReadOnlyNativeSelect = ({ label, value, options = [], className = "" }) => {
  const selectedValue = toText(value);
  const hasSelected = options.some((option) => String(option.value) === selectedValue);
  const resolvedOptions =
    selectedValue && !hasSelected
      ? [{ value: selectedValue, label: selectedValue }, ...options]
      : options;

  return (
    <div className={className}>
      <FieldLabel label={label} />
      <select value={selectedValue} disabled className={controlClass}>
        <option value="">N/A</option>
        {resolvedOptions.map((option) => (
          <option key={`${option.value}-${option.label}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const ReadOnlyReactSelect = ({
  label,
  value,
  options = [],
  fallbackLabel,
  placeholder = "N/A",
  className = "",
  compact = false,
}) => {
  const selected = findSelectedOption(options, value, fallbackLabel);

  return (
    <div className={className}>
      {label && <FieldLabel label={label} />}
      <Select
        options={withSelectedOption(options, selected)}
        value={selected}
        isDisabled
        placeholder={placeholder}
        className={`${label && !compact ? "mt-1 " : ""}text-xs`}
        classNamePrefix="select"
        styles={readOnlySelectStyles}
      />
    </div>
  );
};

const ReadOnlyFileField = ({
  label,
  fileName,
  signedUrl,
  expiresAt,
  onRegenerate,
  canRegenerate = false,
  className = "",
}) => {
  const hasFile = !isBlank(fileName);
  const expired = isFileUrlExpired(expiresAt);
  const canOpenFile = hasFile && signedUrl;
  const showRegenerate = hasFile && canRegenerate && onRegenerate;

  return (
    <div className={className}>
      <FieldLabel label={label} />
      <input
        type="file"
        disabled
        className="mt-1 block w-full border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 text-xs cursor-not-allowed disabled:opacity-100"
      />
      <div className="mt-1 min-h-[18px] text-xs">
        {hasFile ? (
          <div className="flex flex-wrap items-center gap-2">
            {canOpenFile ? (
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {fileName}
              </a>
            ) : (
              <span className="text-gray-900 break-all">{fileName}</span>
            )}
            {showRegenerate && (
              <button
                type="button"
                onClick={onRegenerate}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Regenerate URL</span>
              </button>
            )}
            {expired && (
              <span className="text-gray-500">URL expired</span>
            )}
          </div>
        ) : (
          <span className="text-gray-500">N/A</span>
        )}
      </div>
    </div>
  );
};

const ReadOnlyTableInput = ({ value }) => (
  <input
    type="text"
    value={toText(value)}
    readOnly
    disabled
    placeholder="N/A"
    className={tableControlClass}
  />
);

const TabSection = ({ title, children }) => (
  <section className="mb-6 border-b border-gray-100 pb-5 last:mb-0 last:border-b-0 last:pb-0">
    <h2 className="text-base font-semibold text-gray-900 mb-4">{title}</h2>
    {children}
  </section>
);

const fieldGridClass = "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4";

const emptyAddressOptions = {
  homeRegions: [],
  homeProvinces: [],
  homeTownCities: [],
  homeBarangays: [],
  provincialRegions: [],
  provincialProvinces: [],
  provincialTownCities: [],
  provincialBarangays: [],
};

const formatAddressOptions = (items = [], valueKey, labelKey, extraFields = () => ({})) =>
  items
    .filter((item) => !isBlank(item?.[valueKey]))
    .map((item) => ({
      value: item[valueKey],
      label: toText(item[labelKey] || item[valueKey]),
      ...extraFields(item),
    }));

const PHYSICIAN_TRN_DEFAULT_FILTERS = {
  search: "",
  trans_date_from: "",
  trans_date_to: "",
  loa_no: "",
  trans_no: "",
  provider_name: "",
  member_name: "",
  claim_status: "",
  loa_amount_min: "",
  loa_amount_max: "",
  claim_amount_min: "",
  claim_amount_max: "",
  pf_min: "",
  pf_max: "",
};

const PHYSICIAN_TRN_SORT_OPTIONS = [
  { value: "trans_date", label: "Trans Date" },
  { value: "loa_no", label: "LOA No" },
  { value: "trans_no", label: "TRN No" },
  { value: "provider_name", label: "Provider Name" },
  { value: "member_name", label: "Member Name" },
  { value: "loa_amount", label: "LOA Amount" },
  { value: "claim_status", label: "Claim Status" },
  { value: "claim_amount", label: "Claim Amount" },
  { value: "physician_id", label: "Physician ID" },
  { value: "physician_name", label: "Physician Name" },
  { value: "pf", label: "PF" },
];

const emptyPhysicianTrnPagination = {
  total: 0,
  per_page: 10,
  current_page: 1,
  last_page: 1,
  from: 0,
  to: 0,
};

const formatDisplayValue = (value) => {
  const text = toText(value);
  return text === "" ? "-" : text;
};

const formatDisplayDateTime = (value) => {
  const text = toText(value).replace("T", " ");
  if (!text || text.startsWith("0000-00-00")) return "-";
  return text.length > 19 ? text.slice(0, 19) : text;
};

const formatAmountValue = (value) => {
  if (isBlank(value)) return "-";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return toText(value);

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const resolveDownloadFilename = (response, fallback) => {
  const disposition =
    response?.headers?.["content-disposition"] ||
    response?.headers?.["Content-Disposition"] ||
    "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
    } catch (error) {
      return utf8Match[1].replace(/"/g, "");
    }
  }

  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
};

const ViewPhysician = () => {
  const { physicianId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("information");
  const [physicianData, setPhysicianData] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState({
    update_for: "",
    effectivity_date: "",
    reason: "",
    remarks: "",
    lastupdate_date: "",
    update_by: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [physicianTrns, setPhysicianTrns] = useState([]);
  const [physicianTrnPagination, setPhysicianTrnPagination] = useState(
    emptyPhysicianTrnPagination
  );
  const [physicianTrnPage, setPhysicianTrnPage] = useState(1);
  const [physicianTrnPerPage, setPhysicianTrnPerPage] = useState(10);
  const [physicianTrnFilters, setPhysicianTrnFilters] = useState(
    PHYSICIAN_TRN_DEFAULT_FILTERS
  );
  const [physicianTrnSort, setPhysicianTrnSort] = useState({
    sort_by: "trans_date",
    sort_direction: "desc",
  });
  const [isPhysicianTrnLoading, setIsPhysicianTrnLoading] = useState(false);
  const [isPhysicianTrnExporting, setIsPhysicianTrnExporting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [banks, setBanks] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [subSpecializations, setSubSpecializations] = useState([]);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [addressOptions, setAddressOptions] = useState(emptyAddressOptions);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const buildPhysicianTrnParams = (includePagination = true) => {
    const params = {
      ...physicianTrnFilters,
      sort_by: physicianTrnSort.sort_by,
      sort_direction: physicianTrnSort.sort_direction,
    };

    if (includePagination) {
      params.page = physicianTrnPage;
      params.per_page = physicianTrnPerPage;
    }

    Object.keys(params).forEach((key) => {
      if (isBlank(params[key])) {
        delete params[key];
      }
    });

    return params;
  };

  useEffect(() => {
    if (activeTab !== "physician_trns" || !physicianId) return undefined;

    let isCurrent = true;
    const timeoutId = setTimeout(async () => {
      setIsPhysicianTrnLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/physicians/${physicianId}/trns`, {
          headers: getAuthHeaders(),
          params: buildPhysicianTrnParams(true),
        });

        if (!isCurrent) return;

        const rows = response.data?.data || response.data?.physician_trns || [];
        const pagination = response.data?.pagination || {};
        setPhysicianTrns(rows);
        setPhysicianTrnPagination({
          total: Number(pagination.total ?? response.data?.total_count ?? rows.length),
          per_page: Number(pagination.per_page ?? physicianTrnPerPage),
          current_page: Number(pagination.current_page ?? physicianTrnPage),
          last_page: Number(pagination.last_page ?? 1),
          from: Number(pagination.from ?? 0),
          to: Number(pagination.to ?? rows.length),
        });
      } catch (error) {
        if (!isCurrent) return;
        setPhysicianTrns([]);
        setPhysicianTrnPagination(emptyPhysicianTrnPagination);
        toast.error(
          "Failed to fetch physician TRNs: " +
            (error.response?.data?.message || error.message)
        );
      } finally {
        if (isCurrent) {
          setIsPhysicianTrnLoading(false);
        }
      }
    }, 250);

    return () => {
      isCurrent = false;
      clearTimeout(timeoutId);
    };
  }, [
    activeTab,
    physicianId,
    physicianTrnPage,
    physicianTrnPerPage,
    physicianTrnFilters,
    physicianTrnSort,
  ]);

  useEffect(() => {
    const fetchPhysician = async () => {
      setIsLoading(true);
      try {
        const [physicianResponse, maintenanceResponse] = await Promise.all([
          axios.get(`${BASE_URL}/physicians/${physicianId}`, {
            headers: getAuthHeaders(),
          }),
          axios
            .get(`${BASE_URL}/physicians/${physicianId}/maintenance`, {
              headers: getAuthHeaders(),
            })
            .catch(() => null),
        ]);

        setPhysicianData(physicianResponse.data.data);
        const maintenance = maintenanceResponse?.data?.data?.maintenance || {};
        setMaintenanceData({
          update_for: maintenance.update_for || "",
          effectivity_date: maintenance.effectivity_date || "",
          reason: maintenance.reason || "",
          remarks: maintenance.remarks || "",
          lastupdate_date: maintenance.lastupdate_date || "",
          update_by: maintenance.update_by || "",
        });
      } catch (error) {
        toast.error(
          "Failed to fetch physician details: " +
            (error.response?.data?.message || error.message)
        );
        setTimeout(() => navigate("/physicians"), 2000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhysician();
  }, [physicianId, navigate]);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/providers/list/for-dropdown-select`, {
          headers: getAuthHeaders(),
        });
        setProviders(formatProviderOptions(response.data?.data?.providers || []));
      } catch (error) {
        toast.error(
          "Failed to load providers: " + (error.response?.data?.message || error.message)
        );
      }
    };

    fetchProviders();
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const headers = getAuthHeaders();
      const [banksResponse, nationalitiesResponse, specializationsResponse] =
        await Promise.allSettled([
          axios.get(`${BASE_URL}/bank/get-banks`, { headers }),
          axios.get(`${BASE_URL}/physicians/nationalities`, { headers }),
          axios.get(`${BASE_URL}/physicians/specializations`, { headers }),
        ]);

      if (banksResponse.status === "fulfilled") {
        setBanks(
          (banksResponse.value.data?.data?.banks || []).map((bank) => ({
            value: bank.bank_id,
            label: bank.bank_name,
          }))
        );
      }

      if (nationalitiesResponse.status === "fulfilled") {
        setNationalities(
          (nationalitiesResponse.value.data?.data || []).map((nationality) => ({
            value: nationality.nationality,
            label: nationality.nationality,
          }))
        );
      }

      if (specializationsResponse.status === "fulfilled") {
        const specs = (specializationsResponse.value.data?.data || []).filter(
          (item) => item.status_flag === 1
        );
        setSpecializations(
          specs.map((specialization) => ({
            value: specialization.specialization,
            label: specialization.specialization,
          }))
        );
        setSubSpecializations(
          specs
            .map((specialization) => ({
              value: specialization.sub_specialization,
              label: specialization.sub_specialization,
            }))
            .filter((specialization) => specialization.value)
        );
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (!physicianData) {
      setAddressOptions(emptyAddressOptions);
      return;
    }

    let isCurrent = true;
    const headers = getAuthHeaders();
    const fetchAddressOptions = async (params, valueKey, labelKey, extraFields) => {
      const response = await axios.get(`${BASE_URL}/address/get-ref-address`, {
        headers,
        params,
      });

      return formatAddressOptions(
        response.data?.data || [],
        valueKey,
        labelKey,
        extraFields
      );
    };

    const resolveAddressOptions = async () => {
      const addressRequests = [
        fetchAddressOptions({}, "region_code", "region"),
        physicianData.home_region
          ? fetchAddressOptions(
              { region_code: physicianData.home_region },
              "province_code",
              "province"
            )
          : Promise.resolve([]),
        physicianData.home_province
          ? fetchAddressOptions(
              { province_code: physicianData.home_province },
              "city_code",
              "city"
            )
          : Promise.resolve([]),
        physicianData.home_towncity
          ? fetchAddressOptions(
              { city_code: physicianData.home_towncity },
              "baranggay_code",
              "baranggay",
              (item) => ({ postalcode: item.postcode })
            )
          : Promise.resolve([]),
        physicianData.provincial_region
          ? fetchAddressOptions(
              { region_code: physicianData.provincial_region },
              "province_code",
              "province"
            )
          : Promise.resolve([]),
        physicianData.provincial_province
          ? fetchAddressOptions(
              { province_code: physicianData.provincial_province },
              "city_code",
              "city"
            )
          : Promise.resolve([]),
        physicianData.provincial_towncity
          ? fetchAddressOptions(
              { city_code: physicianData.provincial_towncity },
              "baranggay_code",
              "baranggay",
              (item) => ({ postalcode: item.postcode })
            )
          : Promise.resolve([]),
      ];

      const results = await Promise.allSettled(addressRequests);
      if (!isCurrent) return;

      const getOptions = (index) =>
        results[index].status === "fulfilled" ? results[index].value : [];
      const regions = getOptions(0);

      setAddressOptions({
        homeRegions: regions,
        homeProvinces: getOptions(1),
        homeTownCities: getOptions(2),
        homeBarangays: getOptions(3),
        provincialRegions: regions,
        provincialProvinces: getOptions(4),
        provincialTownCities: getOptions(5),
        provincialBarangays: getOptions(6),
      });

      if (results.some((result) => result.status === "rejected")) {
        toast.error("Failed to load some address labels.");
      }
    };

    resolveAddressOptions();

    return () => {
      isCurrent = false;
    };
  }, [
    physicianData?.home_region,
    physicianData?.home_province,
    physicianData?.home_towncity,
    physicianData?.provincial_region,
    physicianData?.provincial_province,
    physicianData?.provincial_towncity,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const regenerateUrl = async (field) => {
    const uploadId = physicianData?.[`${field}_upload_id`];
    if (!uploadId) {
      toast.error("No upload id to regenerate.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/physicians/document/regenerate-url`,
        { upload_id: uploadId },
        { headers: getAuthHeaders() }
      );
      setPhysicianData((prev) => ({
        ...prev,
        [`${field}_signed_url`]: response.data.data.signed_url || "",
        [`${field}_expires_at`]: response.data.data.expires_at || "",
      }));
      toast.success("URL regenerated successfully.");
    } catch (error) {
      toast.error(
        "Failed to regenerate URL: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateBankProofUrl = async () => {
    if (!physicianData?.bank_proof_upload_id) {
      toast.error("No bank proof file uploaded.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${BASE_URL}/physicians/bank-proof/regenerate-url`,
        { upload_id: physicianData.bank_proof_upload_id },
        { headers: getAuthHeaders() }
      );

      setPhysicianData((prev) => ({
        ...prev,
        bank_proof_signed_url: response.data.signed_url || "",
        bank_proof_expires_at: response.data.expires_at || "",
      }));
      toast.success("Bank proof URL regenerated successfully.");
    } catch (error) {
      toast.error(
        "Failed to regenerate bank proof URL: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintPhysician = () => {
    const opened = openPhysicianPrintPreview({
      physicianData,
      physicianId,
    });

    if (!opened) {
      toast.error("Popup blocked. Please allow popups to open print preview.");
    }
  };

  const searchText = searchQuery.trim().toLowerCase();
  const filteredAffiliations =
    physicianData?.provider_affiliations?.filter((affiliation) => {
      const provider = providers.find(
        (option) => option.value === affiliation?.provider_code
      );
      const values = [
        affiliation?.provider_code,
        affiliation?.provider_name,
        provider?.name,
        provider?.label,
        affiliation?.room_no,
        affiliation?.schedule,
        affiliation?.remarks,
      ];

      return values
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchText);
    }) || [];

  const totalItems = filteredAffiliations.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedAffiliations = filteredAffiliations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePhysicianTrnFilterChange = (field, value) => {
    setPhysicianTrnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPhysicianTrnPage(1);
  };

  const handlePhysicianTrnPerPageChange = (e) => {
    setPhysicianTrnPerPage(Number(e.target.value));
    setPhysicianTrnPage(1);
  };

  const handlePhysicianTrnPageChange = (page) => {
    const lastPage = Math.max(1, Number(physicianTrnPagination.last_page || 1));
    if (page >= 1 && page <= lastPage) {
      setPhysicianTrnPage(page);
    }
  };

  const handlePhysicianTrnSortChange = (field, value) => {
    setPhysicianTrnSort((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPhysicianTrnPage(1);
  };

  const clearPhysicianTrnFilters = () => {
    setPhysicianTrnFilters(PHYSICIAN_TRN_DEFAULT_FILTERS);
    setPhysicianTrnSort({
      sort_by: "trans_date",
      sort_direction: "desc",
    });
    setPhysicianTrnPage(1);
  };

  const refreshPhysicianTrns = () => {
    setPhysicianTrnFilters((prev) => ({ ...prev }));
  };

  const exportPhysicianTrns = async () => {
    try {
      setIsPhysicianTrnExporting(true);
      const response = await axios.get(
        `${BASE_URL}/physicians/${physicianId}/trns/export`,
        {
          headers: getAuthHeaders(),
          params: buildPhysicianTrnParams(false),
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        resolveDownloadFilename(
          response,
          `physician_trns_${physicianId}_${Date.now()}.csv`
        )
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Physician TRNs exported.");
    } catch (error) {
      toast.error(
        "Failed to export physician TRNs: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsPhysicianTrnExporting(false);
    }
  };

  const viewPhysicianTrn = (row) => {
    if (!row?.trans_no) {
      toast.error("No TRN number available.");
      return;
    }

    navigate(`/transactions/${encodeURIComponent(String(row.trans_no))}/details`);
  };

  const getProviderFallbackLabel = (affiliation) => {
    const providerName = affiliation?.provider_name || affiliation?.provider_code || "";
    return providerName && affiliation?.provider_code
      ? `${providerName} (${affiliation.provider_code})`
      : providerName;
  };

  const physicianDocumentEntries = [
    {
      key: "pef",
      label: "Authorization Letter for Settlement",
      fileName:
        physicianData?.pef_file_name ||
        physicianData?.pef_legacy_file ||
        physicianData?.pef_upload_id,
      signedUrl: physicianData?.pef_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "pef",
        fallbackUrl: physicianData?.pef_legacy_file,
        candidates: [physicianData?.pef_legacy_file, physicianData?.pef_file_name],
      }),
      expiresAt: physicianData?.pef_expires_at,
      isExpired: isFileUrlExpired(physicianData?.pef_expires_at),
      canRegenerate: !!physicianData?.pef_upload_id,
      onRegenerate: () => regenerateUrl("pef"),
    },
    {
      key: "bank_proof",
      label: "Bank Proof",
      fileName:
        physicianData?.bank_proof_file_name ||
        physicianData?.bank_proof_legacy_file ||
        physicianData?.bank_proof_upload_id,
      signedUrl: physicianData?.bank_proof_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "physician_2303",
        fallbackUrl: physicianData?.bank_proof_legacy_file,
        candidates: [
          physicianData?.bank_proof_legacy_file,
          physicianData?.bank_proof_file_name,
        ],
      }),
      expiresAt: physicianData?.bank_proof_expires_at,
      isExpired: isFileUrlExpired(physicianData?.bank_proof_expires_at),
      canRegenerate: !!physicianData?.bank_proof_upload_id,
      onRegenerate: regenerateBankProofUrl,
    },
    {
      key: "sworn_attachment",
      label: "Sworn Attachment",
      fileName:
        physicianData?.sworn_attachment_file_name ||
        physicianData?.sworn_attachment_legacy_file ||
        physicianData?.sworn_attachment_upload_id,
      signedUrl: physicianData?.sworn_attachment_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "sworn_attachments",
        fallbackUrl: physicianData?.sworn_attachment_legacy_file,
        candidates: [
          physicianData?.sworn_attachment_legacy_file,
          physicianData?.sworn_attachment_file_name,
        ],
      }),
      expiresAt: physicianData?.sworn_attachment_expires_at,
      isExpired: isFileUrlExpired(physicianData?.sworn_attachment_expires_at),
      canRegenerate: !!physicianData?.sworn_attachment_upload_id,
      onRegenerate: () => regenerateUrl("sworn_attachment"),
    },
    {
      key: "conforme_file",
      label: "Conforme File",
      fileName:
        physicianData?.conforme_file_file_name ||
        physicianData?.conforme_file_legacy_file ||
        physicianData?.conforme_file_upload_id,
      signedUrl: physicianData?.conforme_file_signed_url,
      legacyUrl: resolveLegacyUploadUrl({
        folder: "conforme_file",
        fallbackUrl: physicianData?.conforme_file_legacy_file,
        candidates: [
          physicianData?.conforme_file_legacy_file,
          physicianData?.conforme_file_file_name,
        ],
      }),
      expiresAt: physicianData?.conforme_file_expires_at,
      isExpired: isFileUrlExpired(physicianData?.conforme_file_expires_at),
      canRegenerate: !!physicianData?.conforme_file_upload_id,
      onRegenerate: () => regenerateUrl("conforme_file"),
    },
  ];

  const hasPhysicianDocuments = physicianDocumentEntries.some(
    (documentEntry) =>
      !!(documentEntry.fileName || documentEntry.signedUrl || documentEntry.legacyUrl)
  );
  const physicianDocumentEntryByKey = physicianDocumentEntries.reduce(
    (entries, documentEntry) => ({
      ...entries,
      [documentEntry.key]: documentEntry,
    }),
    {}
  );

  if (!physicianData && !isLoading) {
    return <div className="text-center py-4">Physician not found.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-4 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-xl font-semibold text-gray-900">
                View Physician: {physicianData?.last_name}, {physicianData?.first_name}
              </h1>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/physician")}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-xs"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  <span>Back</span>
                </button>
                <button
                  type="button"
                  onClick={handlePrintPhysician}
                  disabled={!physicianData || isLoading}
                  className="flex items-center gap-1 bg-teal-600 text-white px-3 py-1.5 rounded-md hover:bg-teal-700 transition-colors text-xs disabled:opacity-50"
                >
                  <PrinterIcon className="h-5 w-5" />
                  <span>Print</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDocumentsModal(true)}
                  disabled={!hasPhysicianDocuments || isLoading}
                  className="flex items-center gap-1 bg-indigo-600 text-white px-3 py-1.5 rounded-md hover:bg-indigo-700 transition-colors text-xs disabled:opacity-50"
                  title={
                    hasPhysicianDocuments
                      ? "Open physician documents"
                      : "No uploaded documents available"
                  }
                >
                  <FolderOpenIcon className="h-5 w-5" />
                  <span>Download Document</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/physician/edit/${physicianId}`)}
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                  <span>Edit Physician</span>
                </button>
              </div>
            </div>
          </div>

          {showDocumentsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <h2 className="text-sm font-semibold text-gray-900">
                    Physician Documents
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowDocumentsModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="p-4 space-y-3 overflow-y-auto max-h-[70vh]">
                  {physicianDocumentEntries.map((documentEntry) => {
                    const hasVersion1 = !!documentEntry.legacyUrl;
                    const hasVersion2 = !!documentEntry.signedUrl;
                    const hasAnyVersion = hasVersion1 || hasVersion2;
                    const showExpiredBadge =
                      documentEntry.isExpired && hasVersion2 && !hasVersion1;
                    const badgeClass = !hasAnyVersion
                      ? "bg-gray-100 text-gray-600"
                      : showExpiredBadge
                      ? "bg-amber-100 text-amber-700"
                      : hasVersion1 && hasVersion2
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-blue-100 text-blue-700";
                    const badgeText = !hasAnyVersion
                      ? "No File"
                      : showExpiredBadge
                      ? "Expired URL"
                      : hasVersion1 && hasVersion2
                      ? "V1 + V2"
                      : hasVersion2
                      ? "V2 Available"
                      : "V1 Available";

                    return (
                      <div
                        key={documentEntry.key}
                        className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                      >
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <h3 className="text-sm font-semibold text-slate-900">
                            {documentEntry.label}
                          </h3>
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
                          >
                            {badgeText}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 break-all mb-2">
                          {documentEntry.fileName || "No uploaded file available."}
                        </p>
                        {documentEntry.expiresAt && (
                          <p className="text-[11px] text-gray-500 mb-3">
                            URL expires at: {documentEntry.expiresAt}
                          </p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <a
                            href={documentEntry.signedUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs ${
                              documentEntry.signedUrl
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "bg-gray-200 text-gray-500 pointer-events-none"
                            }`}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>View V2.0</span>
                          </a>
                          <a
                            href={documentEntry.legacyUrl || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs ${
                              documentEntry.legacyUrl
                                ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                : "bg-gray-200 text-gray-500 pointer-events-none"
                            }`}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>View V1.0</span>
                          </a>
                          <button
                            type="button"
                            onClick={documentEntry.onRegenerate}
                            disabled={!documentEntry.canRegenerate || isLoading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                            <span>Regenerate URL</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="px-6 py-4">
            <div className="border-b border-gray-200 mb-5">
              <nav className="-mb-px flex gap-6 overflow-x-auto">
                {Object.keys(TAB_LABELS).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`${
                      activeTab === tab
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                ))}
              </nav>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-4">
                <svg
                  className="animate-spin h-6 w-6 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            ) : (
              <>
                {activeTab === "information" && (
                  <TabSection title="Personal Information">
                    <div className={fieldGridClass}>
                      <ReadOnlyInput label="Physician ID" value={physicianData?.physician_id} />
                      <ReadOnlyInput label="Last Name" value={physicianData?.last_name} />
                      <ReadOnlyInput label="First Name" value={physicianData?.first_name} />
                      <ReadOnlyInput label="Middle Name" value={physicianData?.middle_name} />
                      <ReadOnlyInput label="Extension" value={physicianData?.extension} />
                      <ReadOnlyInput label="Mother's Name" value={physicianData?.mothers_name} />
                      <ReadOnlyReactSelect
                        label="Nationality"
                        value={physicianData?.nationality}
                        options={nationalities}
                      />
                      <ReadOnlyInput
                        label="Date of Birth"
                        value={physicianData?.date_of_birth}
                        type="date"
                      />
                      <ReadOnlyInput label="Age" value={physicianData?.age} type="number" />
                      <ReadOnlyNativeSelect
                        label="Civil Status"
                        value={physicianData?.civil_status}
                        options={CIVIL_STATUS_OPTIONS}
                      />
                      <ReadOnlyNativeSelect
                        label="Gender"
                        value={normalizeGenderValue(physicianData?.gender)}
                        options={GENDER_OPTIONS}
                      />
                      <ReadOnlyReactSelect
                        label="Specialization"
                        value={physicianData?.specialization}
                        options={specializations}
                      />
                      <ReadOnlyReactSelect
                        label="Sub Specialization"
                        value={physicianData?.sub_specialization}
                        options={subSpecializations}
                      />
                      <ReadOnlyInput
                        label="IP Prescription Period"
                        value={physicianData?.ip_prescription_period}
                        type="number"
                      />
                      <ReadOnlyInput
                        label="OP Prescription Period"
                        value={physicianData?.op_prescription_period}
                        type="number"
                      />
                      <ReadOnlyTextarea
                        label="Remarks"
                        value={physicianData?.remarks}
                        className="md:col-span-2 xl:col-span-3"
                      />
                    </div>
                  </TabSection>
                )}

                {activeTab === "contact" && (
                  <TabSection title="Contact Information">
                    <div className={fieldGridClass}>
                      <ReadOnlyInput label="Telephone Number" value={physicianData?.tel_no} />
                      <ReadOnlyInput label="Mobile Number" value={physicianData?.mobile_no} />
                      <ReadOnlyInput
                        label="Email Address"
                        value={physicianData?.email_add}
                        type="email"
                      />
                    </div>
                  </TabSection>
                )}

                {activeTab === "bank" && (
                  <TabSection title="Bank Details">
                    <div className={fieldGridClass}>
                      <ReadOnlyInput
                        label="Bank Account Name"
                        value={physicianData?.bank_acc_name}
                      />
                      <ReadOnlyInput
                        label="Bank Account Number"
                        value={physicianData?.bank_acct_no}
                      />
                      <ReadOnlyReactSelect
                        label="Bank"
                        value={physicianData?.bank_id || physicianData?.bank_name}
                        fallbackLabel={physicianData?.bank_name}
                        options={banks}
                      />
                      <ReadOnlyInput label="Bank Branch" value={physicianData?.bank_branch} />
                      <ReadOnlyCheckbox
                        label="Suspected Fraudulent Physician"
                        checked={physicianData?.fraud_flag}
                      />
                      <ReadOnlyFileField
                        label="Authorization Letter for Settlement"
                        fileName={physicianDocumentEntryByKey.pef?.fileName}
                        signedUrl={
                          physicianDocumentEntryByKey.pef?.signedUrl ||
                          physicianDocumentEntryByKey.pef?.legacyUrl
                        }
                        expiresAt={physicianDocumentEntryByKey.pef?.expiresAt}
                        onRegenerate={() => regenerateUrl("pef")}
                        canRegenerate={physicianDocumentEntryByKey.pef?.canRegenerate}
                        className="md:col-span-2"
                      />
                      <ReadOnlyFileField
                        label="Bank Proof"
                        fileName={physicianDocumentEntryByKey.bank_proof?.fileName}
                        signedUrl={
                          physicianDocumentEntryByKey.bank_proof?.signedUrl ||
                          physicianDocumentEntryByKey.bank_proof?.legacyUrl
                        }
                        expiresAt={physicianDocumentEntryByKey.bank_proof?.expiresAt}
                        onRegenerate={regenerateBankProofUrl}
                        canRegenerate={physicianDocumentEntryByKey.bank_proof?.canRegenerate}
                        className="md:col-span-2"
                      />
                    </div>
                  </TabSection>
                )}

                {activeTab === "address" && (
                  <>
                    <TabSection title="Home Address">
                      <div className={fieldGridClass}>
                        <ReadOnlyInput
                          label="Street/Building/Subdivision/Village"
                          value={physicianData?.home_streetno_bldg}
                          className="md:col-span-2 xl:col-span-3"
                        />
                        <ReadOnlyReactSelect
                          label="Region"
                          value={physicianData?.home_region}
                          options={addressOptions.homeRegions}
                        />
                        <ReadOnlyReactSelect
                          label="Province"
                          value={physicianData?.home_province}
                          options={addressOptions.homeProvinces}
                        />
                        <ReadOnlyReactSelect
                          label="Town/City"
                          value={physicianData?.home_towncity}
                          options={addressOptions.homeTownCities}
                        />
                        <ReadOnlyReactSelect
                          label="Barangay"
                          value={physicianData?.home_barangay}
                          options={addressOptions.homeBarangays}
                        />
                        <ReadOnlyInput
                          label="Postal Code"
                          value={physicianData?.home_postalcode}
                        />
                      </div>
                    </TabSection>

                    <TabSection title="Provincial Address">
                      <div className={fieldGridClass}>
                        <ReadOnlyInput
                          label="Street/Building/Subdivision/Village"
                          value={physicianData?.provincial_streetno_bldg}
                          className="md:col-span-2 xl:col-span-3"
                        />
                        <ReadOnlyReactSelect
                          label="Region"
                          value={physicianData?.provincial_region}
                          options={addressOptions.provincialRegions}
                        />
                        <ReadOnlyReactSelect
                          label="Province"
                          value={physicianData?.provincial_province}
                          options={addressOptions.provincialProvinces}
                        />
                        <ReadOnlyReactSelect
                          label="Town/City"
                          value={physicianData?.provincial_towncity}
                          options={addressOptions.provincialTownCities}
                        />
                        <ReadOnlyReactSelect
                          label="Barangay"
                          value={physicianData?.provincial_barangay}
                          options={addressOptions.provincialBarangays}
                        />
                        <ReadOnlyInput
                          label="Postal Code"
                          value={physicianData?.provincial_postalcode}
                        />
                      </div>
                    </TabSection>
                  </>
                )}

                {activeTab === "license" && (
                  <TabSection title="License Details">
                    <div className={fieldGridClass}>
                      <ReadOnlyInput label="TIN Number" value={physicianData?.tin_no} />
                      <ReadOnlyInput label="SSS/GSIS Number" value={physicianData?.sss_no} />
                      <ReadOnlyInput
                        label="Philhealth Number"
                        value={physicianData?.philhealth_no}
                      />
                      <ReadOnlyInput label="PHIC Number" value={physicianData?.phic_no} />
                      <ReadOnlyInput
                        label="PHIC Date From"
                        value={physicianData?.phic_from}
                        type="date"
                      />
                      <ReadOnlyInput
                        label="PHIC Date To"
                        value={physicianData?.phic_to}
                        type="date"
                      />
                      <ReadOnlyInput
                        label="PRC License Number"
                        value={physicianData?.prc_no}
                      />
                      <ReadOnlyInput
                        label="PRC Validity Date"
                        value={physicianData?.prc_expiry}
                        type="date"
                      />
                      <ReadOnlyInput
                        label="Tax Percentage"
                        value={physicianData?.tax_percentage}
                        type="number"
                      />
                      <ReadOnlyNativeSelect
                        label="Vatable"
                        value={normalizeVatableValue(physicianData?.vatable)}
                        options={VATABLE_OPTIONS}
                      />
                      <ReadOnlyFileField
                        label="Sworn Attachment"
                        fileName={physicianDocumentEntryByKey.sworn_attachment?.fileName}
                        signedUrl={
                          physicianDocumentEntryByKey.sworn_attachment?.signedUrl ||
                          physicianDocumentEntryByKey.sworn_attachment?.legacyUrl
                        }
                        expiresAt={physicianDocumentEntryByKey.sworn_attachment?.expiresAt}
                        onRegenerate={() => regenerateUrl("sworn_attachment")}
                        canRegenerate={
                          physicianDocumentEntryByKey.sworn_attachment?.canRegenerate
                        }
                        className="md:col-span-2"
                      />
                      <ReadOnlyFileField
                        label="Conforme File"
                        fileName={physicianDocumentEntryByKey.conforme_file?.fileName}
                        signedUrl={
                          physicianDocumentEntryByKey.conforme_file?.signedUrl ||
                          physicianDocumentEntryByKey.conforme_file?.legacyUrl
                        }
                        expiresAt={physicianDocumentEntryByKey.conforme_file?.expiresAt}
                        onRegenerate={() => regenerateUrl("conforme_file")}
                        canRegenerate={physicianDocumentEntryByKey.conforme_file?.canRegenerate}
                        className="md:col-span-2"
                      />
                    </div>
                  </TabSection>
                )}

                {activeTab === "maintenance" && (
                  <TabSection title="Maintenance">
                    <div className={fieldGridClass}>
                      <ReadOnlyNativeSelect
                        label="Update For"
                        value={maintenanceData.update_for}
                        options={MAINTENANCE_UPDATE_OPTIONS}
                      />
                      <ReadOnlyInput
                        label="Effectivity Date"
                        value={maintenanceData.effectivity_date}
                        type="date"
                      />
                      <ReadOnlyTextarea
                        label="Reason"
                        value={maintenanceData.reason}
                        className="md:col-span-2 xl:col-span-3"
                      />
                      <ReadOnlyTextarea
                        label="Remarks"
                        value={maintenanceData.remarks}
                        className="md:col-span-2 xl:col-span-3"
                      />
                      <ReadOnlyInput
                        label="Last Updated"
                        value={maintenanceData.lastupdate_date}
                      />
                      <ReadOnlyInput label="Updated By" value={maintenanceData.update_by} />
                    </div>
                  </TabSection>
                )}

                {activeTab === "provider_affiliates" && (
                  <TabSection title="Provider Affiliations">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
                      <div className="relative w-full md:max-w-sm">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Search
                        </label>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-8 pr-4 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Search provider, room, schedule, or remarks"
                        />
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-2 top-[29px]" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Show entries
                        </label>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Provider
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Room Number
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Schedule
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Remarks
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedAffiliations.map((affiliation, index) => (
                            <tr key={`${affiliation?.provider_code || "provider"}-${index}`}>
                              <td className="px-4 py-2 text-xs text-gray-900 align-top">
                                <ReadOnlyReactSelect
                                  value={affiliation?.provider_code}
                                  fallbackLabel={getProviderFallbackLabel(affiliation)}
                                  options={providers}
                                  className="min-w-[260px]"
                                  compact
                                />
                              </td>
                              <td className="px-4 py-2 align-top">
                                <ReadOnlyTableInput value={affiliation?.room_no} />
                              </td>
                              <td className="px-4 py-2 align-top">
                                <ReadOnlyTableInput value={affiliation?.schedule} />
                              </td>
                              <td className="px-4 py-2 align-top">
                                <ReadOnlyTableInput value={affiliation?.remarks} />
                              </td>
                            </tr>
                          ))}
                          {paginatedAffiliations.length === 0 && (
                            <tr>
                              <td colSpan="4" className="px-4 py-4 text-xs text-gray-500 text-center">
                                {searchQuery
                                  ? "No matching provider affiliations found."
                                  : "No provider affiliations added."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
                      <div className="text-xs text-gray-600">
                        Showing{" "}
                        {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1 || isLoading}
                          className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <span className="px-3 py-1.5 text-xs text-gray-600">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages || isLoading}
                          className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </TabSection>
                )}

                {activeTab === "physician_trns" && (
                  <TabSection title="Physician TRNs">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Search
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.search}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange("search", e.target.value)
                            }
                            className="w-full pl-8 pr-4 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Search LOA, TRN, provider, member, or status"
                          />
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-2 top-[29px]" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Date From
                          </label>
                          <input
                            type="date"
                            value={physicianTrnFilters.trans_date_from}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "trans_date_from",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Date To
                          </label>
                          <input
                            type="date"
                            value={physicianTrnFilters.trans_date_to}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "trans_date_to",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Claim Status
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.claim_status}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "claim_status",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Claim status"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            LOA No
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.loa_no}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange("loa_no", e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="LOA no"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            TRN No
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.trans_no}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange("trans_no", e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="TRN no"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Provider Name
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.provider_name}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "provider_name",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Provider"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Member Name
                          </label>
                          <input
                            type="text"
                            value={physicianTrnFilters.member_name}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "member_name",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Member"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            LOA Min
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.loa_amount_min}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "loa_amount_min",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            LOA Max
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.loa_amount_max}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "loa_amount_max",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Claim Min
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.claim_amount_min}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "claim_amount_min",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Claim Max
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.claim_amount_max}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange(
                                "claim_amount_max",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            PF Min
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.pf_min}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange("pf_min", e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            PF Max
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={physicianTrnFilters.pf_max}
                            onChange={(e) =>
                              handlePhysicianTrnFilterChange("pf_max", e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Show entries
                            </label>
                            <select
                              value={physicianTrnPerPage}
                              onChange={handlePhysicianTrnPerPageChange}
                              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Sort By
                            </label>
                            <select
                              value={physicianTrnSort.sort_by}
                              onChange={(e) =>
                                handlePhysicianTrnSortChange("sort_by", e.target.value)
                              }
                              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              {PHYSICIAN_TRN_SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Direction
                            </label>
                            <select
                              value={physicianTrnSort.sort_direction}
                              onChange={(e) =>
                                handlePhysicianTrnSortChange(
                                  "sort_direction",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="desc">Descending</option>
                              <option value="asc">Ascending</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={refreshPhysicianTrns}
                            disabled={isPhysicianTrnLoading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <ArrowPathIcon
                              className={`h-4 w-4 ${
                                isPhysicianTrnLoading ? "animate-spin" : ""
                              }`}
                            />
                            <span>Refresh</span>
                          </button>
                          <button
                            type="button"
                            onClick={clearPhysicianTrnFilters}
                            disabled={isPhysicianTrnLoading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                          >
                            <XMarkIcon className="h-4 w-4" />
                            <span>Clear</span>
                          </button>
                          <button
                            type="button"
                            onClick={exportPhysicianTrns}
                            disabled={isPhysicianTrnExporting || isPhysicianTrnLoading}
                            className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-md hover:bg-emerald-700 transition-colors text-xs disabled:opacity-50"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            <span>
                              {isPhysicianTrnExporting ? "Exporting..." : "Export CSV"}
                            </span>
                          </button>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="min-w-[1500px] w-full border border-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Trans Date
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                LOA No
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                TRN No
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Provider Name
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Member Name
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                LOA Amount
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Claim Status
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Claim Amount
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Physician ID
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Physician Name
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                PF
                              </th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {isPhysicianTrnLoading ? (
                              <tr>
                                <td
                                  colSpan="12"
                                  className="px-4 py-6 text-center text-xs text-gray-500"
                                >
                                  Loading physician TRNs...
                                </td>
                              </tr>
                            ) : physicianTrns.length > 0 ? (
                              physicianTrns.map((row, index) => (
                                <tr
                                  key={`${row?.trans_no || "trn"}-${
                                    row?.loa_no || "loa"
                                  }-${index}`}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                                    {formatDisplayDateTime(row?.trans_date)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                                    {formatDisplayValue(row?.loa_no)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                                    {formatDisplayValue(row?.trans_no)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 min-w-[220px]">
                                    {formatDisplayValue(row?.provider_name)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 min-w-[220px]">
                                    {formatDisplayValue(row?.member_name)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 text-right whitespace-nowrap">
                                    {formatAmountValue(row?.loa_amount)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                                    {formatDisplayValue(row?.claim_status)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 text-right whitespace-nowrap">
                                    {formatAmountValue(row?.claim_amount)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                                    {formatDisplayValue(row?.physician_id)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 min-w-[220px]">
                                    {formatDisplayValue(row?.physician_name)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-900 text-right whitespace-nowrap">
                                    {formatAmountValue(row?.pf)}
                                  </td>
                                  <td className="px-3 py-2 text-center whitespace-nowrap">
                                    <button
                                      type="button"
                                      onClick={() => viewPhysicianTrn(row)}
                                      disabled={!row?.trans_no}
                                      className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                      <span>View</span>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="12"
                                  className="px-4 py-6 text-center text-xs text-gray-500"
                                >
                                  No physician TRNs found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs text-gray-600">
                          Showing {physicianTrnPagination.from || 0} to{" "}
                          {physicianTrnPagination.to || 0} of{" "}
                          {physicianTrnPagination.total || 0} entries
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handlePhysicianTrnPageChange(physicianTrnPage - 1)
                            }
                            disabled={physicianTrnPage === 1 || isPhysicianTrnLoading}
                            className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1.5 text-xs text-gray-600">
                            Page {physicianTrnPagination.current_page || physicianTrnPage}{" "}
                            of {physicianTrnPagination.last_page || 1}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              handlePhysicianTrnPageChange(physicianTrnPage + 1)
                            }
                            disabled={
                              physicianTrnPage >=
                                Number(physicianTrnPagination.last_page || 1) ||
                              isPhysicianTrnLoading
                            }
                            className="px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    </div>
                  </TabSection>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ViewPhysician;
